import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { NavController } from '@ionic/angular';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ErrorService } from 'src/app/services/error.service';
import { ToastService } from 'src/app/services/toast.service';
import { TranslationService } from 'src/app/services/translation.service';
import { environment } from 'src/environments/environment';
import { SettlementDeed } from '../../pages/settlement-deeds/types';
import * as SettlementDeedActions from '../actions/settlement-deed.actions';
import { AppState } from '../app.reducer';

@Injectable()
export class SettlementDeedEffects {
  constructor(
    public actions$: Actions,
    public http: HttpClient,
    private errorService: ErrorService,
    private store: Store<AppState>,
    private toastService: ToastService,
    private navController: NavController,
    private translationService: TranslationService,
    private router: Router,
  ) { }

  getSettlementDeedById$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettlementDeedActions.getSettlementDeedById),
      switchMap((data) => {
        SettlementDeedActions.settlementDeedLoading({ payload: { loading: true } });
        return this.http

          .get<any>(environment.apiUrl + '/settlementDeeds/' + data.payload.id)
          .pipe(
            map((result) => {
              return result.data;
            }),
            switchMap((responseData) => {
              this.store.dispatch(
                SettlementDeedActions.getSettlementDeedByIdSuccess({
                  payload: {
                    settlementDeed: responseData,
                  },
                })
              );
              return of(
                SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              this.store.dispatch(
                SettlementDeedActions.settlementDeedFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
              return of(
                SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } })
              );
            })
          );
      })
    )
  );

  createSettlementDeed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettlementDeedActions.createSettlementDeed),
      switchMap((data) => {
        this.store.dispatch(
          SettlementDeedActions.settlementDeedLoading({ payload: { loading: true } })
        );
        return this.http
          .post<null>(environment.apiUrl + '/createSettlementDeed', data.payload)
          .pipe(
            switchMap((payload: { data: SettlementDeed; errors: string }) => {
              this.navController.navigateBack('/settlement-deeds/' + payload.data.id);
              this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.UPDATE')}`);
              return of(
                SettlementDeedActions.settlementDeedCreated(),
                SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              return of(
                SettlementDeedActions.settlementDeedFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  updateSettlementDeed$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(SettlementDeedActions.editSettlementDeed),
      switchMap(({ payload }) => {
        this.store.dispatch(
          SettlementDeedActions.settlementDeedLoading({ payload: { loading: true } })
        );
        return this.http
          .put<null>(
            environment.apiUrl + `/settlementDeeds/${payload.id}`,
            payload.context
          )
          .pipe(
            switchMap(() => {
              this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.UPDATE')}`);
              return of(
                SettlementDeedActions.editSettlementDeedSuccess(),
                SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              return of(
                SettlementDeedActions.settlementDeedFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    );
  });

  deleteSettlementDeed$ = createEffect(() =>
    this.actions$.pipe(
      ofType(SettlementDeedActions.deleteSettlementDeed),
      switchMap((payload) => {
        SettlementDeedActions.settlementDeedLoading({ payload: { loading: true } });
        return this.http

          .delete<any>(environment.apiUrl + '/settlementDeeds/' + payload.payload.id)
          .pipe(
            map((x) => {
              return x.data;
            }),
            switchMap((responseData) => {
              this.navController.back();
              this.store.dispatch(
                SettlementDeedActions.deleteSettlementDeedSuccess({
                  payload: {
                    settlementDeed: responseData,
                  },
                })
              );
              this.router.navigate(['/settlement-deeds']);
              this.toastService.showToast(`${this.translationService.translate('STATUS_UPDATE.DELETE')}`);
              return of(
                SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } })
              );
            }),
            catchError((error) => {
              this.store.dispatch(SettlementDeedActions.settlementDeedLoading({ payload: { loading: false } }));
              return of(
                SettlementDeedActions.settlementDeedFail({
                  payload: { error: this.errorService.mapError(error) },
                })
              );
            })
          );
      })
    )
  );
}


